import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { Check } from "@phosphor-icons/react";
import LocationBadge, { updateLocationCache } from "@/components/LocationBadge";

const BUSINESS_TYPES = [
  "Marca de Ropa",
  "Marca para el Hogar",
  "Tienda Minorista",
  "Distribuidora",
  "Marca de Alimentos",
  "Tienda Online",
  "Negocio de Servicios",
  "Agencia Creativa",
  "Otro",
];

const CURRENCIES = ["ARS", "CLP", "COP", "USD", "MXN", "PAB", "PEN"];

export default function Configuracion() {
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const [countries, setCountries] = useState([]);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/countries"), api.get("/settings")])
      .then(([c, s]) => { setCountries(c.data.countries); setSettings(s.data); })
      .catch(() => toast.error("No se pudo cargar la configuración"));
  }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        country: settings.country,
        state: settings.state,
        city: settings.city,
        business_type: settings.business_type,
        business_name: settings.business_name,
        currency: settings.currency,
        theme,
      });
      setSettings(data);
      updateLocationCache({ country: data.country || null, city: data.city || null });
      toast.success("Preferencias guardadas");
    } catch { toast.error("No se pudo guardar"); }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="text-sm text-muted-foreground">Cargando…</div>;

  return (
    <div data-testid="page-configuracion" className="max-w-3xl">
      <header className="mb-8">
        <div className="label-mono text-muted-foreground">Configuración</div>
        <h1 className="display text-5xl md:text-6xl mt-1">Preferencias</h1>
      </header>

      <section className="brut-card mb-6 space-y-4">
        <div className="label-mono text-muted-foreground">Cuenta</div>
        <div>
          <div className="text-xs text-muted-foreground">Nombre</div>
          <div className="font-semibold" data-testid="settings-user-name">{user?.name}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Correo</div>
          <div className="font-semibold break-all">{user?.email}</div>
        </div>
        {(settings.country || settings.city) && (
          <div>
            <div className="text-xs text-muted-foreground mb-1">Ubicación</div>
            <LocationBadge
              country={settings.country}
              city={settings.city}
              testid="settings-location-badge"
            />
          </div>
        )}
        <button onClick={logout} className="btn-secondary" data-testid="btn-logout-settings">Cerrar sesión</button>
      </section>

      <section className="brut-card mb-6 space-y-4">
        <div className="label-mono text-muted-foreground">Negocio</div>
        <label className="block">
          <div className="label-mono mb-1">Nombre del negocio</div>
          <input
            className="input-brut"
            value={settings.business_name || ""}
            onChange={(e) => setSettings({ ...settings, business_name: e.target.value })}
            data-testid="settings-business-name"
          />
        </label>
        <label className="block">
          <div className="label-mono mb-1">Tipo de negocio</div>
          <select
            className="input-brut"
            value={settings.business_type || ""}
            onChange={(e) => setSettings({ ...settings, business_type: e.target.value })}
            data-testid="settings-business-type"
          >
            <option value="">Sin especificar</option>
            {BUSINESS_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>
        <div className="grid md:grid-cols-2 gap-4">
          <label className="block">
            <div className="label-mono mb-1">País</div>
            <select
              className="input-brut"
              value={settings.country || ""}
              onChange={(e) => {
                const nextCountry = e.target.value;
                if (nextCountry === settings.country) return;
                setSettings({ ...settings, country: nextCountry, state: "", city: "" });
              }}
              data-testid="select-pais"
            >
              {countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="label-mono mb-1">Moneda</div>
            <select
              className="input-brut"
              value={settings.currency || ""}
              onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
              data-testid="settings-currency"
            >
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="label-mono mb-1">Estado / Provincia</div>
            <input
              className="input-brut"
              value={settings.state || ""}
              onChange={(e) => setSettings({ ...settings, state: e.target.value })}
              data-testid="settings-state"
            />
          </label>
          <label className="block">
            <div className="label-mono mb-1">Ciudad</div>
            <input
              className="input-brut"
              value={settings.city || ""}
              onChange={(e) => setSettings({ ...settings, city: e.target.value })}
              data-testid="settings-city"
            />
          </label>
        </div>
      </section>

      <section className="brut-card mb-6">
        <div className="label-mono text-muted-foreground mb-3">Tema</div>
        <div className="flex gap-3">
          {["light", "dark"].map((t) => (
            <button
              key={t}
              onClick={() => setTheme(t)}
              data-testid={`theme-${t}`}
              className={`btn-secondary flex-1 flex items-center justify-center gap-2 ${theme === t ? "!bg-foreground !text-background" : ""}`}
            >
              {theme === t && <Check size={14} weight="bold" />}
              {t === "light" ? "Claro" : "Oscuro"}
            </button>
          ))}
        </div>
      </section>

      <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="btn-save-settings">
        <Check size={14} weight="bold" /> {saving ? "Guardando…" : "Guardar cambios"}
      </button>
    </div>
  );
}
